import { useCallback, useEffect, useMemo } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { now } from "@/shared/utils/date/dateUtils"
import { trackSettingsChanged } from "@/shared/services/analyticsTrackers"

// ---------------------------------------------------------------------------
// localStorage cache for boolean settings — prevents flash-of-wrong-layout
// ---------------------------------------------------------------------------

const BOOL_CACHE_PREFIX = "totodo:sb:"

/**
 * Settings whose value is a secret (a feed token, private calendar URLs): their change is tracked
 * without the value, which analytics would otherwise receive in part.
 */
const SECRET_SETTINGS = new Set(["#calendar_feed_token", "#ical-calendars"])

/** Synchronously read cached boolean setting (true = entry existed last time) */
function readBoolCache(id: string): boolean {
  try {
    return localStorage.getItem(`${BOOL_CACHE_PREFIX}${id}`) === "1"
  } catch {
    return false
  }
}

/** Write boolean setting to localStorage (mirror of Dexie state) */
function writeBoolCache(id: string, value: boolean): void {
  try {
    const key = `${BOOL_CACHE_PREFIX}${id}`
    if (value) {
      localStorage.setItem(key, "1")
    } else {
      localStorage.removeItem(key)
    }
  } catch {
    // localStorage unavailable — ignore
  }
}

// ---------------------------------------------------------------------------
// useSetting — synced user preferences (db.setting)
// ---------------------------------------------------------------------------

/**
 * Read/write a single synced setting from `db.setting`.
 *
 * - Auto-save: `setValue` writes immediately — no save button needed.
 * - Existence-based booleans: `resetToDefault` deletes the entry entirely.
 *
 * @param id           Setting key, e.g. `"#language"`, `"#flat-today"`
 * @param defaultValue Value returned when no entry exists in DB
 *
 * @example
 * const { value, setValue, isLoading } = useSetting("#language", "system")
 */
export function useSetting<T = string>(id: string, defaultValue: T) {
  const [row, , isLoading] = useLiveQueryImmediate(
    () => db.setting.get(id),
    ["setting", id],
    undefined,
  )

  const value: T = useMemo(() => {
    if (!row) return defaultValue
    if (row.value === undefined || row.value === "") return defaultValue
    try {
      return JSON.parse(row.value) as T
    } catch {
      // If not valid JSON, return the raw string (common for simple string values)
      return row.value as T
    }
  }, [row, defaultValue])

  const setValue = useCallback(
    async (v: T) => {
      const timestamp = now()
      const serialized = typeof v === "string" ? v : JSON.stringify(v)
      await db.setting.put({
        id,
        value: serialized,
        device: "",
        createdAt: row?.createdAt ?? timestamp,
        createdBy: row?.createdBy ?? "",
        updatedAt: timestamp,
        updatedBy: "",
      })
      trackSettingsChanged(id, SECRET_SETTINGS.has(id) ? "[redacted]" : serialized)
    },
    [id, row?.createdAt, row?.createdBy],
  )

  const resetToDefault = useCallback(async () => {
    await db.setting.delete(id)
  }, [id])

  return useMemo(
    () => ({
      /** Current value (or defaultValue if no DB entry) */
      value,
      /** Whether the initial DB read is still in progress */
      isLoading,
      /** Persists a new value immediately */
      setValue,
      /** Deletes the DB entry, reverting to defaultValue */
      resetToDefault,
    }),
    [value, isLoading, setValue, resetToDefault],
  )
}

/**
 * Read a boolean setting using existence-based logic.
 *
 * - Entry exists → `true` (non-default)
 * - Entry missing → `false` (default)
 *
 * Uses a localStorage cache so the value is available synchronously on
 * first render — prevents flash-of-wrong-layout (e.g. grouped→flat Today).
 *
 * @example
 * const { value, toggle, isLoading } = useSettingBoolean("#flat-today")
 */
export function useSettingBoolean(id: string) {
  // Use cached boolean as placeholder so first render is correct
  const cachedPlaceholder = useMemo(() => {
    return readBoolCache(id)
      ? { id, value: "1", device: "", createdAt: "", createdBy: "" }
      : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const [row, , isLoading] = useLiveQueryImmediate(
    () => db.setting.get(id),
    ["setting-bool", id],
    cachedPlaceholder,
  )

  const value = !!row

  // Keep localStorage cache in sync with real Dexie data
  useEffect(() => {
    if (isLoading) return
    writeBoolCache(id, value)
  }, [id, value, isLoading])

  const toggle = useCallback(async () => {
    if (row) {
      writeBoolCache(id, false)
      await db.setting.delete(id)
      trackSettingsChanged(id, "off")
    } else {
      writeBoolCache(id, true)
      await db.setting.put({
        id,
        value: "1",
        device: "",
        createdAt: now(),
        createdBy: "",
      })
      trackSettingsChanged(id, "on")
    }
  }, [id, row])

  const setValue = useCallback(
    async (v: boolean) => {
      writeBoolCache(id, v)
      if (v) {
        await db.setting.put({
          id,
          value: "1",
          device: "",
          createdAt: now(),
          createdBy: "",
        })
      } else {
        await db.setting.delete(id)
      }
    },
    [id],
  )

  return useMemo(
    () => ({
      /** true if DB entry exists, false otherwise */
      value,
      /** Whether the initial DB read is still in progress */
      isLoading,
      /** Toggles between exists/not-exists */
      toggle,
      /** Explicitly set true (create) or false (delete) */
      setValue,
    }),
    [value, isLoading, toggle, setValue],
  )
}
